import { useEffect, useMemo, useRef, useState } from "react";
import { StreamingMarkdown, TextContent } from "./markdownStreaming";
import ShareButton from "./shareButton";

const CHARS_PER_FRAME = 10;

function getSafeMarkdownLength(text, limit) {
  const candidate = text.slice(0, limit);
  const trailingToken = candidate.match(/\S+$/)?.[0] || "";

  // Una URL no se publica hasta que recibe un delimitador; evita autolinks parciales.
  if (/^(https?:\/\/|www\.)/i.test(trailingToken)) {
    return candidate.length - trailingToken.length;
  }

  let safeLength = 0;
  for (let index = 0; index < candidate.length; index += 1) {
    if (/\s|[.,;:!?]/.test(candidate[index])) {
      safeLength = index + 1;
    }
  }

  return safeLength;
}

/**
 * Renderiza el contenido de UN turno conversacional, exclusivamente la
 * parte de pregunta + respuesta del asistente. Las notas relacionadas
 * se manejan aparte en el panel lateral derecho (`list-wrapper-results`).
 *
 * IMPORTANTE: este componente NO renderiza el wrapper `.summary-card`,
 * solo el contenido. El wrapper común vive en `Results` para que toda
 * la conversación quede dentro de un único contenedor (estilo ChatGPT).
 */
export default function TurnContent({ turn, parentBase, isLast }) {
  const { query, resume, isStreaming, hasReceivedChunk, answerError } = turn;

  const [fullResume, setFullResume] = useState(isStreaming ? "" : resume || "");
  const [showStreaming, setShowStreaming] = useState(!!isStreaming);

  const streamingDomRef = useRef(null);
  const indexRef = useRef(0);
  const rafRef = useRef(null);
  const scheduleTickRef = useRef(null);
  const targetTextRef = useRef(resume || "");
  const renderedLengthRef = useRef(0);
  const prevStreamingRef = useRef(isStreaming);
  const resumeRef = useRef(resume);

  useEffect(() => { resumeRef.current = resume; }, [resume]);

  const displayMarkdown = useMemo(() => fullResume || resume || "", [fullResume, resume]);
  const streamingSource = resume || "";

  // Inicio de streaming
  useEffect(() => {
    if (isStreaming && !prevStreamingRef.current) {
      setShowStreaming(true);
      setFullResume("");
      indexRef.current = 0;
      renderedLengthRef.current = 0;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    prevStreamingRef.current = isStreaming;
  }, [isStreaming]);

  // Actualiza el objetivo sin reiniciar la animación ya en curso.
  useEffect(() => {
    targetTextRef.current = streamingSource;
    scheduleTickRef.current?.();
  }, [streamingSource]);

  // Un único ciclo RAF consume el texto disponible y se reactiva solo si llegan más chunks.
  useEffect(() => {
    if (!isStreaming) return undefined;

    const tick = () => {
      rafRef.current = null;
      const targetText = targetTextRef.current;
      if (indexRef.current >= targetText.length) return;

      const updateMarkdown = streamingDomRef.current?.__updateMarkdown;
      if (!updateMarkdown) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      indexRef.current = Math.min(indexRef.current + CHARS_PER_FRAME, targetText.length);
      const safeLength = getSafeMarkdownLength(targetText, indexRef.current);

      if (safeLength > renderedLengthRef.current) {
        renderedLengthRef.current = safeLength;
        updateMarkdown(targetText.slice(0, safeLength));
      }

      if (indexRef.current < targetTextRef.current.length) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    const scheduleTick = () => {
      if (!rafRef.current && indexRef.current < targetTextRef.current.length) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    scheduleTickRef.current = scheduleTick;
    scheduleTick();

    return () => {
      scheduleTickRef.current = null;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [isStreaming]);

  // Fin de streaming → fijar contenido estático
  useEffect(() => {
    if (!isStreaming && showStreaming) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setFullResume(resumeRef.current || "");
      setShowStreaming(false);
    }
  }, [isStreaming, showStreaming]);

  // Indicador solo hasta que el primer chunk comienza a llegar.
  const showWaitingDots = isLast && isStreaming && !hasReceivedChunk;

  return (
    <article
      className={`conversation-turn${isStreaming ? " is-streaming" : ""}`}
      data-turn-id={turn.id}
      aria-label={`Turno: ${query}`}
    >
      {/* Pregunta del usuario - mantiene la estructura original de summary-title-wrap */}
      <header className="summary-header turn-question">
        <div className="summary-title-wrap">
          <h3 className="summary-title">{query}</h3>
          <span className="summary-icon" aria-hidden="true">
            <svg
              className="summary-icon-svg"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
              fill="none"
              stroke="#333333"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M7 3h10a2 2 0 0 1 2 2v16l-7-4-7 4V5a2 2 0 0 1 2-2z" />
              <path d="M12 9.2l.35.95.95.35-.95.35-.35.95-.35-.95-.95-.35.95-.35.35-.95z" />
            </svg>
          </span>
        </div>
      </header>

      {/* Respuesta del asistente */}
      <div
        className="summary-text-wrap turn-answer"
      >
        {showStreaming && (
          <StreamingMarkdown targetRef={streamingDomRef} parentBase={parentBase} />
        )}

        {!showStreaming && displayMarkdown && (
          <TextContent markdown={displayMarkdown} parentBase={parentBase} />
        )}

        {showWaitingDots && (
          <span
            className="streaming-indicator"
            title="Recibiendo datos en tiempo real"
            aria-label="Escribiendo"
          >
            <div className="loading-text"></div>
          </span>
        )}
      </div>

      {!isStreaming && answerError && (
        <div className="turn-error" role="alert">
          <p>{answerError}</p>
        </div>
      )}

      {!isStreaming && !answerError && (
        <footer className="turn-actions">
          <ShareButton query={query} />
        </footer>
      )}
    </article>
  );
}